import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, X } from 'lucide-react';
import { useLoadScript } from '@react-google-maps/api';
import { Input } from '../../../shared/components/ui/input';
import { Button } from '../../../shared/components/ui/button';
import { env } from '@/core/config/env';
import { GOOGLE_MAPS_LIBRARIES, toLocationData, type LocationData } from '../utils/geocodedLocation';

type LocationSearchProps = {
  onLocationSelect: (location: LocationData | null) => void;
  placeholder?: string;
  value?: string;
  apiKey?: string;
};

export const LocationSearch = ({
  onLocationSelect,
  placeholder = 'Enter city or ZIP code',
  value: controlledValue,
  apiKey,
}: LocationSearchProps) => {
  const [inputValue, setInputValue] = useState(controlledValue || '');
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState<'down' | 'up'>('down');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(240);

  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderServiceRef = useRef<google.maps.Geocoder | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || env.GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Initialize Google Maps services when loaded
  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      geocoderServiceRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  // Update input value when controlled value changes
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInputValue(controlledValue);
    }
  }, [controlledValue]);

  // Fetch place predictions
  const fetchPredictions = useCallback((searchText: string) => {
    if (!autocompleteServiceRef.current || searchText.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: searchText,
        types: ['(regions)'],
        componentRestrictions: { country: 'us' },
      },
      (
        predictions: google.maps.places.AutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatus,
      ) => {
        setIsLoading(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setIsOpen(true);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);
          setIsOpen(true);
        } else {
          setSuggestions([]);
          setIsOpen(false);
        }
      },
    );
  }, []);

  // Debounced search
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedIndex(-1);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce API calls
    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  };

  // Geocode place and call onLocationSelect
  const selectPlace = useCallback(
    async (placeId: string) => {
      if (!geocoderServiceRef.current) {
        return;
      }

      geocoderServiceRef.current.geocode(
        { placeId },
        async (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus,
        ) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results[0]) {
            const locationData = await toLocationData(results[0]);
            onLocationSelect(locationData);
            setInputValue(locationData.address);
          }
        },
      );

      setIsOpen(false);
      setSuggestions([]);
    },
    [onLocationSelect],
  );

  // Handle suggestion click
  const handleSuggestionClick = (prediction: google.maps.places.AutocompletePrediction) => {
    selectPlace(prediction.place_id);
  };

  // Handle clear button
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    onLocationSelect(null);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev,
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          selectPlace(suggestions[selectedIndex].place_id);
        }
        break;

      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;

      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-location-search]')) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const desiredHeight = 240;
    const minHeight = 120;

    if (spaceBelow < desiredHeight && spaceAbove > spaceBelow) {
      setDropdownPlacement('up');
      setDropdownMaxHeight(
        Math.max(minHeight, Math.min(desiredHeight, spaceAbove - 8)),
      );
      return;
    }

    setDropdownPlacement('down');
    setDropdownMaxHeight(
      Math.max(minHeight, Math.min(desiredHeight, spaceBelow - 8)),
    );
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateDropdownPosition();
  }, [isOpen, suggestions.length, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleResize = () => updateDropdownPosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, updateDropdownPosition]);

  // Show error state if Google Maps fails to load (check error FIRST)
  if (loadError) {
    return (
      <div>
        <label className="text-sm text-gray-700 mb-2 block">Location</label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Error loading Google Maps"
            disabled
            value=""
            className="pl-10 text-sm"
          />
        </div>
      </div>
    );
  }

  // Show loading state while Google Maps script loads
  if (!isLoaded) {
    return (
      <div>
        <label className="text-sm text-gray-700 mb-2 block">Location</label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Loading Google Maps..."
            disabled
            value=""
            className="pl-10 text-sm"
          />
        </div>
      </div>
    );
  }

  return (
    <div data-location-search className="relative" ref={containerRef}>
      <label className="text-sm text-gray-700 mb-2 block">Location</label>
      
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10 text-sm"
          aria-label="Location search"
          role="textbox"
        />

        {inputValue && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            aria-label="Clear location"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && (
        <div
          className={`absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-y-auto ${
            dropdownPlacement === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          style={{ maxHeight: `${dropdownMaxHeight}px` }}
        >
          {isLoading ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              Searching...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              No locations found
            </div>
          ) : (
            <ul role="listbox" aria-label="Location suggestions">
              {suggestions.map((prediction, index) => (
                <li
                  key={prediction.place_id}
                  role="option"
                  aria-selected={index === selectedIndex}
                  className={`
                    px-3 py-2 cursor-pointer text-sm transition-colors
                    ${index === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}
                  `}
                  onClick={() => handleSuggestionClick(prediction)}
                >
                  <div className="font-medium text-gray-900">
                    {prediction.structured_formatting.main_text}
                  </div>
                  <div className="text-xs text-gray-600">
                    {prediction.structured_formatting.secondary_text}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
